import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Save } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import MediaUploader from '../../components/MediaUploader';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const HeroManager = () => {
  const [heroSection, setHeroSection] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    highlight: '',
    background_type: 'video',
    background_video: '',
    background_image: '',
    overlay_opacity: 0.5,
    text_color: '#ffffff'
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchHeroSection();
  }, []);

  const fetchHeroSection = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/cms/sections?page=home`);
      const hero = response.data.find(section => section.section_key === 'hero');
      
      if (hero) {
        setHeroSection(hero);
        setFormData({
          title: hero.content.title || '',
          highlight: hero.content.highlight || '',
          background_type: hero.content.background_type || 'video',
          background_video: hero.content.background_video || '',
          background_image: hero.content.background_image || '',
          overlay_opacity: hero.content.overlay_opacity || 0.5,
          text_color: hero.content.text_color || '#ffffff'
        });
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching hero section:', error);
      toast({
        title: "Error",
        description: "Failed to load hero section",
        variant: "destructive"
      });
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!heroSection) return;

    setSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/cms/sections/${heroSection.id}`, {
        content: formData
      });

      toast({
        title: "Success",
        description: "Hero section updated successfully"
      });

      fetchHeroSection();
    } catch (error) {
      console.error('Error updating hero section:', error);
      toast({
        title: "Error",
        description: "Failed to update hero section",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-center py-12">Loading hero section...</div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-light text-[#2d2d2d]">Hero Section Management</h1>
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-green-600 hover:bg-green-700 text-white"
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      <div className="bg-white p-6 rounded-lg shadow-lg space-y-6">
        {/* Text Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-normal text-[#2d2d2d] mb-2">Title</label>
            <Input
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Welcome to"
            />
          </div>
          <div>
            <label className="block text-sm font-normal text-[#2d2d2d] mb-2">Highlight Text</label>
            <Input
              value={formData.highlight}
              onChange={(e) => setFormData({ ...formData, highlight: e.target.value })}
              placeholder="Ambition."
            />
          </div>
        </div>

        {/* Background Type */}
        <div>
          <label className="block text-sm font-normal text-[#2d2d2d] mb-2">Background Type</label>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="background_type"
                value="video"
                checked={formData.background_type === 'video'}
                onChange={(e) => setFormData({ ...formData, background_type: e.target.value })}
                className="w-4 h-4"
              />
              <span className="text-[#2d2d2d]">Video</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="background_type"
                value="image"
                checked={formData.background_type === 'image'}
                onChange={(e) => setFormData({ ...formData, background_type: e.target.value })}
                className="w-4 h-4"
              />
              <span className="text-[#2d2d2d]">Image</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="background_type"
                value="color"
                checked={formData.background_type === 'color'}
                onChange={(e) => setFormData({ ...formData, background_type: e.target.value })}
                className="w-4 h-4"
              />
              <span className="text-[#2d2d2d]">Solid Color</span>
            </label>
          </div>
        </div>

        {/* Background Media */}
        {formData.background_type === 'video' && (
          <MediaUploader
            label="Background Video"
            currentUrl={formData.background_video}
            onMediaSelect={(url) => setFormData({ ...formData, background_video: url })}
            accept="video/*"
          />
        )}

        {formData.background_type === 'image' && (
          <MediaUploader
            label="Background Image"
            currentUrl={formData.background_image}
            onMediaSelect={(url) => setFormData({ ...formData, background_image: url })}
            accept="image/*"
          />
        )}

        {formData.background_type === 'color' && (
          <div>
            <label className="block text-sm font-normal text-[#2d2d2d] mb-2">Background Color</label>
            <div className="flex gap-2 items-center">
              <input
                type="color"
                value={formData.background_color || '#0A1A50'}
                onChange={(e) => setFormData({ ...formData, background_color: e.target.value })}
                className="w-20 h-10 rounded cursor-pointer"
              />
              <Input
                value={formData.background_color || '#0A1A50'}
                onChange={(e) => setFormData({ ...formData, background_color: e.target.value })}
                placeholder="#0A1A50"
                className="flex-1"
              />
            </div>
          </div>
        )}

        {/* Styling Options */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-normal text-[#2d2d2d] mb-2">
              Overlay Opacity (0 = transparent, 1 = opaque)
            </label>
            <Input
              type="number"
              min="0"
              max="1"
              step="0.1"
              value={formData.overlay_opacity}
              onChange={(e) => setFormData({ ...formData, overlay_opacity: parseFloat(e.target.value) })}
            />
          </div>
          <div>
            <label className="block text-sm font-normal text-[#2d2d2d] mb-2">Text Color</label>
            <div className="flex gap-2 items-center">
              <input
                type="color"
                value={formData.text_color}
                onChange={(e) => setFormData({ ...formData, text_color: e.target.value })}
                className="w-20 h-10 rounded cursor-pointer"
              />
              <Input
                value={formData.text_color}
                onChange={(e) => setFormData({ ...formData, text_color: e.target.value })}
                placeholder="#ffffff"
                className="flex-1"
              />
            </div>
          </div>
        </div>

        {/* Preview Note */}
        <div className="bg-blue-50 p-4 rounded-lg">
          <p className="text-sm text-blue-800">
            💡 <strong>Tip:</strong> Changes will be visible on the homepage after saving. Visit the homepage to see your changes!
          </p>
        </div>
      </div>
    </div>
  );
};

export default HeroManager;
